import type { DataNode } from 'components/WorkspaceEditor/types'
import {
    HIGHLIGHT_DURATION,
    HIGHLIGHT_STYLES,
    getContentSelectors,
} from './constants'

/**
 * Finds a content element in the DOM using multiple selectors
 * @param contentId - The ID of the content to find
 * @return The first matching HTMLElement, or null if not found
 */
export const findContentElement = (contentId: string): HTMLElement | null => {
    const selectors = getContentSelectors(contentId)

    for (const selector of selectors) {
        try {
            const element = document.querySelector(selector) as HTMLElement
            if (element) return element
        } catch (error) {
            // silently ignore invalid selectors
        }
    }

    return null
}

/**
 * Applies visual highlight to newly created content
 * @param element - The container HTML element to highlight
 */
export const highlightElement = (element: HTMLElement) => {
    const originalTransition = element.style.transition

    // apply highlight
    Object.assign(element.style, HIGHLIGHT_STYLES)

    // remove highlight after duration
    setTimeout(() => {
        element.style.transition = originalTransition
        element.style.border = 'unset'
    }, HIGHLIGHT_DURATION)
}

type FindTreeNodePathResult = {
    nodeKey: string
    pathToNode: string[]
} | null

/**
 * Finds tree node path for content ID
 * @param nodes - Array of tree nodes to search
 * @param targetId - The ID of the content to find
 * @param currentPath - Current path being traversed
 * @return The path to the node and its key, or null if not found
 */
export const findTreeNodePath = (
    nodes: DataNode[],
    targetId: string,
    currentPath: string[] = []
): FindTreeNodePathResult => {
    for (const node of nodes) {
        const nodePath = [...currentPath, node.key]

        // check direct key match
        if (node.key === targetId) {
            return { nodeKey: node.key, pathToNode: nodePath }
        }

        // check content item nodes
        if (node.contentItemNodes?.some((item) => item.id === targetId)) {
            return { nodeKey: node.key, pathToNode: nodePath }
        }

        // check root article ID
        if (node.rootArticleId === targetId) {
            return { nodeKey: node.key, pathToNode: nodePath }
        }

        // check children recursively
        if (node.children?.length) {
            const childResult = findTreeNodePath(
                node.children,
                targetId,
                nodePath
            )
            if (childResult) return childResult
        }
    }

    return null
}

/**
 * Checks if an element is visible within the tree's scrollable container
 * @param element - The DOM element to check
 * @return true if element is sufficiently visible on scroll container, false otherwise
 */
export const isElementVisible = (element: HTMLElement): boolean => {
    const scrollContainer = document.querySelector('.ant-tree-list-holder')
    if (!scrollContainer) return false

    const elementRect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    return (
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom
    )
}
